/**
 * Hook: GET /api/t/{slug}/intelligence/summary
 *
 * Hiring Intelligence dashboard — single summary payload. The redesign replaced
 * the per-panel chart endpoints with one benchmark-anchored metric set: every
 * tile on the grid reads a field on this response. Trend objects carry a prior
 * value + a direction so the UI can colour the chip by *directional goodness*,
 * not raw sign (down-is-good for ratio / time metrics).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";

export type IntelRange = "7d" | "30d" | "90d";

/**
 * DC2 screening-cap threshold state emitted by the intelligence summary payload.
 * "none" (< 80%), "warn80" (>= 80%), "at_cap" (>= 100%).
 */
type ScreeningThresholdState = "none" | "warn80" | "at_cap";

type IntelPersona = "agency" | "in_house";

/** A measured value with a period-over-period trend. */
interface IntelMetric {
  value: number | null;
  prior: number | null;
  trend_pct: number | null;
  trend_dir: "up" | "down" | "flat" | null;
}

/** A metric judged against a single target. */
interface IntelTargetMetric extends IntelMetric {
  benchmark: number;
}

/**
 * Time-to-first-interview: a target metric that also reports which stage the
 * p50 was measured against. "interview" = into interview_scheduled (primary);
 * "shortlist" = phone_screen fallback when the window has no into-interview
 * events. The UI relabels the tile from the basis.
 */
interface IntelTimeToFirstMetric extends IntelTargetMetric {
  metric_basis?: "interview" | "shortlist";
}

/** AI first-pass reads — a measured count, no benchmark. */
type IntelCountMetric = IntelMetric;

/** Fill-rate carries the raw opened/filled counts behind the ratio. */
interface IntelFillRate extends IntelMetric {
  opened: number;
  filled: number;
}

/** Pipeline coverage is judged against a healthy band. */
interface IntelBandMetric extends IntelMetric {
  band_lo: number;
  band_hi: number;
}

/** Time-to-decision p50 is judged against a goal. */
interface IntelGoalMetric extends IntelMetric {
  goal: number;
}

/** A single funnel stage: a 0–1 conversion ratio judged against a band. */
export interface FunnelStage extends IntelMetric {
  band_lo: number;
  band_hi: number;
}

interface FunnelConversion {
  screen_to_interview: FunnelStage;
  interview_to_offer: FunnelStage;
  offer_to_accept: FunnelStage;
}

/**
 * A single stage in the hiring funnel as a real COUNT (events into that stage
 * over the window), NOT a conversion ratio. Ordered top→bottom (Screened →
 * Interviewing → Offer → Hired); each downstream stage is a subset flow of the
 * prior, so the series is naturally non-increasing — a true descending funnel.
 */
interface FunnelStageCount {
  key: string;
  label: string;
  count: number;
}

export interface IntelSummary {
  active_candidates: number;
  active_jds: number;
  /** Candidates currently in an active interview stage (interview_scheduled or
   *  interviewed) — powers the Dashboard "Interviews this week" KPI. */
  interviews_this_week: number;

  // DC2 screening cap — null ceiling → unlimited / unconfigured (meter hides).
  screen_cap_ceiling: number | null;
  screen_cap_used: number;
  screen_cap_pct: number | null;
  screen_cap_threshold_state: ScreeningThresholdState;

  // AI tiles — neutral, never red.
  ai_agreement_pct: number | null;
  ai_first_pass_reads: IntelCountMetric;

  // Agency metrics.
  screen_to_interview_ratio: IntelTargetMetric;
  time_to_first_submittal_days: IntelTimeToFirstMetric;
  fill_rate: IntelFillRate;

  // In-house metrics.
  pipeline_coverage: IntelBandMetric;
  time_to_decision_p50_days: IntelGoalMetric;
  funnel_conversion: FunnelConversion;
  /** Descending stage COUNTS for the hiring funnel chart (real events-into-stage). */
  funnel_stage_counts: FunnelStageCount[];

  persona: IntelPersona;
  has_data: boolean;
  per_panel_has_data: Record<string, boolean>;
}

/**
 * @param range  metric window (default 30d).
 * @param jdId   Dashboard role picker: when set, the KPI summary scopes
 *   `active_candidates` + `interviews_this_week` to that JD's pipeline (Open
 *   roles / `active_jds` always stays workspace-wide). Omit (undefined) for the
 *   all-roles selection and the Intelligence dashboard → workspace-wide totals.
 *   Threaded onto the query key so switching roles re-queries.
 */
export function useIntelligenceSummary(
  range: IntelRange = "30d",
  jdId?: string,
): UseQueryResult<IntelSummary, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return useQuery<IntelSummary, ApiError>({
    queryKey: ["intelligence", "summary", tenantSlug, range, jdId ?? null],
    queryFn: async () => {
      const r = await apiClient.get<IntelSummary>(
        `/api/t/${tenantSlug}/intelligence/summary`,
        { params: { range, ...(jdId ? { jd: jdId } : {}) } },
      );
      return r.data;
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
  });
}
